// src/admin/AdminSocialDetails.jsx
import { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "../services/mysqlStore";
import { db } from "../services/mysqlStore";

const FIELDS = [
  { key: "name",      label: "Company Name",     placeholder: "Circuit Printing Solutions", icon: "🏢", type: "text" },
  { key: "logoUrl",   label: "Company Logo URL", placeholder: "/logo.jpeg or https://...",       icon: "🖼️", type: "url" },
  { key: "phone",     label: "Phone Number",    placeholder: "0800 123 4567",              icon: "📞", type: "tel" },
  { key: "whatsapp",  label: "WhatsApp Number", placeholder: "07700 900 000",              icon: "💬", type: "tel" },
  { key: "email",     label: "Email Address",   placeholder: "info@circuitprinting.ca", icon: "✉️", type: "email" },
  { key: "address",   label: "Address Line 1",  placeholder: "Unit 5, Print Park",         icon: "📍", type: "text" },
  { key: "city",      label: "City / Region",   placeholder: "Toronto, Canada",            icon: "🏙️", type: "text" },
  { key: "state",     label: "State / Province", placeholder: "Ontario",                    icon: "🗺️", type: "text" },
  { key: "postcode",  label: "Postal Code",      placeholder: "M1A 1A1",                     icon: "📮", type: "text" },
  { key: "country",   label: "Country",          placeholder: "Canada",                      icon: "🌎", type: "text" },
  { key: "taxId",     label: "Tax / HST Number", placeholder: "Optional",                    icon: "🧾", type: "text" },
  { key: "hours",     label: "Business Hours",  placeholder: "Mon–Fri 8am–6pm, Sat 9am–2pm", icon: "🕐", type: "text" },
  { key: "facebook",  label: "Facebook URL",    placeholder: "https://facebook.com/yourpage",  icon: "📘", type: "url" },
  { key: "instagram", label: "Instagram URL",   placeholder: "https://instagram.com/yourpage", icon: "📸", type: "url" },
  { key: "twitter",   label: "Twitter / X URL", placeholder: "https://twitter.com/yourpage",   icon: "🐦", type: "url" },
  { key: "linkedin",  label: "LinkedIn URL",    placeholder: "https://linkedin.com/company/yourpage", icon: "💼", type: "url" },
  { key: "youtube",   label: "YouTube URL",     placeholder: "https://youtube.com/@yourchannel",      icon: "▶️", type: "url" },
  { key: "tiktok",    label: "TikTok URL",      placeholder: "https://tiktok.com/@yourpage",          icon: "🎵", type: "url" },
];

const EMPTY = Object.fromEntries(FIELDS.map(f => [f.key, ""]));

export default function AdminSocialDetails() {
  const [form, setForm]       = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState("");

  useEffect(function() {
    async function load() {
      try {
        var snap = await getDoc(doc(db, "siteSettings", "socialDetails"));
        if (snap.exists()) {
          setForm(function(prev) { return { ...prev, ...snap.data() }; });
        }
      } catch(e) {
        setError("Failed to load settings.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleSave() {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      await setDoc(doc(db, "siteSettings", "socialDetails"), form);
      setSaved(true);
      setTimeout(function() { setSaved(false); }, 3000);
    } catch(e) {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  function handleChange(key, value) {
    setForm(function(prev) { return { ...prev, [key]: value }; });
  }

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px", gap: 12, flexDirection: "column" }}>
        <div style={{ width: 32, height: 32, border: "3px solid rgba(255,255,255,0.1)", borderTop: "3px solid var(--cyan)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13 }}>Loading settings...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  var contactFields = FIELDS.filter(function(f) { return ["name","phone","whatsapp","email","address","city","state","postcode","country","taxId","hours"].indexOf(f.key) !== -1; });
  var socialFields  = FIELDS.filter(function(f) { return ["facebook","instagram","twitter","linkedin","youtube","tiktok"].indexOf(f.key) !== -1; });

  return (
    <div style={{ width: "100%", maxWidth: "none", padding: "8px 0 40px" }}>
      <style>{"@media (max-width: 760px) { .social-settings-grid { grid-template-columns: 1fr !important; } }"}</style>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ color: "#fff", fontSize: 22, fontWeight: 700, margin: 0 }}>🌐 Social &amp; Contact Details</h2>
        <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 13, marginTop: 6 }}>
          Saved details automatically appear in the footer, Contact page, and across the website.
        </p>
      </div>

      {/* Contact Info Section */}
      <div style={sectionStyle}>
        <div style={sectionHeadStyle}>
          <span style={dotStyle("#00AADD")} /> Contact Information
        </div>
        <div className="social-settings-grid" style={gridStyle}>
          {contactFields.map(function(f) {
            return (
              <div key={f.key} style={fieldWrapStyle(f.key === "hours" || f.key === "address")}>
                <label style={labelStyle}>{f.icon} {f.label}</label>
                <input
                  type={f.type}
                  value={form[f.key]}
                  onChange={function(e) { handleChange(f.key, e.target.value); }}
                  placeholder={f.placeholder}
                  style={inputStyle}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Social Links Section */}
      <div style={sectionStyle}>
        <div style={sectionHeadStyle}>
          <span style={dotStyle("#E91E8C")} /> Social Media Links
        </div>
        <div className="social-settings-grid" style={gridStyle}>
          {socialFields.map(function(f) {
            return (
              <div key={f.key} style={fieldWrapStyle(false)}>
                <label style={labelStyle}>{f.icon} {f.label}</label>
                <input
                  type={f.type}
                  value={form[f.key]}
                  onChange={function(e) { handleChange(f.key, e.target.value); }}
                  placeholder={f.placeholder}
                  style={inputStyle}
                />
              </div>
            );
          })}
        </div>
        <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 12, marginTop: 14 }}>
          Tip: Leave a social link blank if you do not want it displayed in the footer.
        </p>
      </div>

      {/* Save Button */}
      {error && (
        <div style={{ background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)", color: "#f87171", padding: "10px 16px", borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            background: saving ? "rgba(0,170,221,0.4)" : "var(--cyan)",
            color: "#fff",
            border: "none",
            padding: "12px 32px",
            borderRadius: 8,
            fontWeight: 700,
            fontSize: 15,
            cursor: saving ? "not-allowed" : "pointer",
            transition: "background 0.2s"
          }}
        >
          {saving ? "Saving..." : "💾 Save Changes"}
        </button>
        {saved && (
          <span style={{ color: "#4ade80", fontWeight: 600, fontSize: 14, animation: "fadeIn 0.3s ease" }}>
            ✅ Saved successfully!
          </span>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        input::placeholder { color: rgba(255,255,255,0.2) !important; }
        input:focus { border-color: var(--cyan) !important; outline: none; }
      `}</style>
    </div>
  );
}

var sectionStyle = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 12,
  padding: "20px 24px",
  marginBottom: 20
};

var sectionHeadStyle = {
  color: "rgba(255,255,255,0.8)",
  fontWeight: 700,
  fontSize: 14,
  marginBottom: 18,
  display: "flex",
  alignItems: "center",
  gap: 8,
  textTransform: "uppercase",
  letterSpacing: "0.05em"
};

function dotStyle(color) {
  return { width: 8, height: 8, borderRadius: "50%", background: color, display: "inline-block", flexShrink: 0 };
}

var gridStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "14px 20px"
};

function fieldWrapStyle(fullWidth) {
  return { gridColumn: fullWidth ? "1 / -1" : "auto" };
}

var labelStyle = {
  display: "block",
  color: "rgba(255,255,255,0.55)",
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 6,
  textTransform: "uppercase",
  letterSpacing: "0.04em"
};

var inputStyle = {
  width: "100%",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8,
  padding: "10px 14px",
  color: "#fff",
  fontSize: 14,
  boxSizing: "border-box",
  transition: "border-color 0.2s",
  fontFamily: "inherit"
};
